export type UserRole = "Government Health Officer" | "Organization User" | "Admin" | "Viewer";

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  organizationId: string;
  organizationName: string;
  avatar?: string;
  password?: string; // For demo login only — removed at runtime
}

// All demo users with passwords for quick-login
export const allUsers: CurrentUser[] = [
  { id: "U001", name: "Dr. Jean-Pierre Fotso", email: "jp.fotso@minsante.gov.cm", role: "Government Health Officer", organizationId: "ORG001", organizationName: "Ministry of Public Health", password: "demo" },
  { id: "U002", name: "Dr. Amina Ngono", email: "a.ngono@minsante.gov.cm", role: "Admin", organizationId: "ORG001", organizationName: "Ministry of Public Health", password: "demo" },
  { id: "U003", name: "Paul Ekobe", email: "p.ekobe@minsante.gov.cm", role: "Viewer", organizationId: "ORG001", organizationName: "Ministry of Public Health", password: "demo" },
  { id: "U004", name: "Dr. Marie-Claire Biya", email: "m.biya@msf.org", role: "Organization User", organizationId: "ORG002", organizationName: "MSF Cameroon", password: "demo" },
  { id: "U005", name: "Thomas Nkoulou", email: "t.nkoulou@msf.org", role: "Admin", organizationId: "ORG002", organizationName: "MSF Cameroon", password: "demo" },
  { id: "U006", name: "Dr. Sarah Ojong", email: "s.ojong@msf.org", role: "Viewer", organizationId: "ORG002", organizationName: "MSF Cameroon", password: "demo" },
  { id: "U007", name: "Dr. Kwame Mensah", email: "k.mensah@malariaconsortium.org", role: "Organization User", organizationId: "ORG003", organizationName: "Malaria Consortium", password: "demo" },
  { id: "U008", name: "Lucia Abang", email: "l.abang@malariaconsortium.org", role: "Admin", organizationId: "ORG003", organizationName: "Malaria Consortium", password: "demo" },
  { id: "U009", name: "F\u00e9lix Mbounda", email: "f.mbounda@malariaconsortium.org", role: "Viewer", organizationId: "ORG003", organizationName: "Malaria Consortium", password: "demo" },
  { id: "U010", name: "Dr. Honorine Tchuente", email: "h.tchuente@minsante.gov.cm", role: "Government Health Officer", organizationId: "ORG001", organizationName: "Ministry of Public Health", password: "demo" },
];

// Find user by credentials (demo auth)
export function findUserByCredentials(email: string, password: string): CurrentUser | null {
  const user = allUsers.find((u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
  if (!user) return null;
  // Return without password
  const { password: _, ...safeUser } = user;
  return safeUser as CurrentUser;
}

// Find user by email for quick login
export function findUserByEmail(email: string): CurrentUser | null {
  const user = allUsers.find((u) => u.email.toLowerCase() === email.toLowerCase());
  if (!user) return null;
  const { password: _, ...safeUser } = user;
  return safeUser as CurrentUser;
}

// Default logged-in user (fallback when no auth)
export const currentUser: CurrentUser = {
  id: "U001",
  name: "Dr. Jean-Pierre Fotso",
  email: "jp.fotso@minsante.gov.cm",
  role: "Government Health Officer",
  organizationId: "ORG001",
  organizationName: "Ministry of Public Health",
};

// Role-based permission helpers
export function canEditResources(role: UserRole): boolean {
  return role !== "Viewer";
}

export function canManageUsers(role: UserRole): boolean {
  return role === "Admin" || role === "Government Health Officer";
}

export function canManageSettings(role: UserRole): boolean {
  return role === "Admin" || role === "Government Health Officer";
}

export function canManageOrganizations(role: UserRole): boolean {
  return role === "Admin" || role === "Government Health Officer";
}

export function canExport(role: UserRole): boolean {
  return role !== "Viewer";
}

export function canAcknowledgeAlerts(role: UserRole): boolean {
  return role !== "Viewer";
}

export function canInviteUsers(role: UserRole): boolean {
  return role === "Admin" || role === "Government Health Officer";
}

export function canModifyThresholds(role: UserRole): boolean {
  return role === "Admin" || role === "Government Health Officer";
}

export function canManageAPIKeys(role: UserRole): boolean {
  return role === "Admin";
}

export function isAdmin(role: UserRole): boolean {
  return role === "Admin";
}

export function isViewer(role: UserRole): boolean {
  return role === "Viewer";
}

export function getRoleBadgeColor(role: UserRole): string {
  switch (role) {
    case "Government Health Officer":
      return "bg-emerald-500/20 text-emerald border-emerald/30";
    case "Organization User":
      return "bg-teal-500/20 text-teal border-teal/30";
    case "Admin":
      return "bg-amber-500/20 text-amber border-amber/30";
    case "Viewer":
      return "bg-slate-500/20 text-slate-600 dark:text-slate-300 border-slate-500/30";
    default:
      return "bg-slate-500/20 text-slate-600 dark:text-slate-300";
  }
}